/**
 * <QuizReview> — la relecture du quiz de prérequis : ce que l'élève a répondu, question par
 * question, et la bonne réponse (organism).
 *
 * L'ancien `QuizCorrectionReview` (la fin du quiz) et `QuizReviewModal` (« Revoir le quiz »), sur le
 * même `PrerequisiteQuizDisplay`. Une surface pour les deux, posée dans la feuille de Sam
 * (`<SamSheet>`) :
 *  - l'en-tête : « Ton quiz », et la croix ;
 *  - le score (`<QuizScore>`) : l'anneau, « 3/5 », « 60 % », « Quiz réussi » ou « Quiz non
 *    réussi » ;
 *  - « Ta correction » : toutes les questions, pas seulement les fausses, dans une carte-liste
 *    (`<QuizReviewQuestion>`, repliées, qu'on déplie pour lire toutes les réponses) ;
 *  - au bout, à la fin du quiz, la sortie (`endAction`, « Continuer ») : l'activité s'ouvre. Elle est
 *    dans un pied fixe, en bas, comme le bouton du quiz qu'elle suit et la barre de copie de l'énoncé
 *    (Wajih au daily du 21/10) ; la bulle tandem du live se pose au-dessus de ce pied
 *    (`useFloatingBase`), et la fin de la correction remonte au-dessus d'elle (`useFloatingReserve`).
 * Elle charge (`loading`, sa forme), échoue (`error`, « Réessayer »), ou n'a rien à montrer
 * (`review` vide : « Tu n'as pas encore fait ce quiz », comme avant).
 *
 * Contrôlée : la correction, le chargement, l'erreur arrivent en props ; seules les questions
 * dépliées sont un état d'interface.
 *
 * Composes DS pieces only — `<ConversationHeader>`, `<ConversationClose>`, `<QuizScore>`,
 * `<SectionLabel>`, `<CardList>`, `<QuizReviewQuestion>`, `<Banner>`, `<Button>`, `<DelayedSkeleton>`.
 */
import React from "react"
import { View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { ScrollView } from "../../atoms/ScrollView"
import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { CardList, CardListRow } from "../../molecules/CardList"
import { ConversationClose, ConversationHeader } from "../../molecules/ConversationHeader"
import { QuizReviewQuestion } from "../../molecules/QuizReviewQuestion"
import { QuizScore } from "../../molecules/QuizScore"
import { CONVERSATION_MAX_WIDTH } from "../ConversationThread"
import { type UIQuizReview } from "../../../ports/primary/ui/types/studentQuizReview"
import { useFloatingBase, useFloatingReserve } from "../../templates/PageTemplate/FloatingSlot"
import { QuizReviewSkeleton } from "./QuizReviewSkeleton"

// La forme de la relecture vit dans `ports/primary/ui/types`.
export type { UIQuizReview }

export type QuizReviewProps = {
    /** La correction. `null` (ni chargement ni erreur) : l'élève n'a pas fait le quiz. */
    review: UIQuizReview | null
    /** La correction se lit. Défaut `false`. */
    loading?: boolean
    /** La correction n'a pas pu se lire. Défaut `false`. */
    error?: boolean
    /** « Réessayer », sous l'erreur. Absent : pas de bouton. */
    onRetry?: () => void
    /** La croix. */
    onClose: () => void
    /** La sortie au bout — « Continuer », à la fin du quiz. Absente : la croix seule. */
    endAction?: { label: string; onPress: () => void }
    /** Les questions dépliées d'emblée, par id (une story). Défaut : aucune, comme avant. */
    defaultExpandedIds?: number[]
    /**
     * Le pied dégage lui-même l'indicateur d'accueil. Défaut `true` (dans une feuille) ; une page
     * qui pose déjà sa zone sûre passe `false`.
     */
    bottomInset?: boolean
    testID?: string
}

const TEXTS = {
    title: "Ton quiz",
    close: "Fermer la correction",
    correction: "Ta correction",
    empty: "Tu n'as pas encore fait ce quiz.",
    error: "Impossible de récupérer la correction du quiz.",
    retry: "Réessayer",
    loading: "Chargement de la correction",
}

export function QuizReview(props: QuizReviewProps) {
    const {
        review,
        loading = false,
        error = false,
        onRetry,
        onClose,
        endAction,
        defaultExpandedIds,
        bottomInset = true,
        testID,
    } = props
    const insets = useSafeAreaInsets()
    const floatingRoom = useFloatingReserve()
    const footerBase = useFloatingBase(endAction != null)

    let body: React.ReactNode
    if (loading) {
        body = (
            <DelayedSkeleton accessibilityLabel={TEXTS.loading}>
                <QuizReviewSkeleton />
            </DelayedSkeleton>
        )
    } else if (error) {
        body = (
            <Stack gap="3" align="center">
                <Banner message={TEXTS.error} color="danger" icon="exclamation-circle" tinted />
                {onRetry != null && <Button label={TEXTS.retry} onPress={onRetry} />}
            </Stack>
        )
    } else if (review == null) {
        body = (
            <Text color="text-muted" center>
                {TEXTS.empty}
            </Text>
        )
    } else {
        body = (
            <Stack gap="4">
                <QuizScore
                    score={review.score}
                    total={review.total}
                    percentage={review.percentage}
                    passed={review.passed}
                />
                <Stack gap="2">
                    <SectionLabel>{TEXTS.correction}</SectionLabel>
                    <CardList>
                        {review.questions.map((question, index) => (
                            <CardListRow key={question.id} py="0">
                                <QuizReviewQuestion
                                    index={index}
                                    question={question}
                                    defaultExpanded={defaultExpandedIds?.includes(question.id)}
                                />
                            </CardListRow>
                        ))}
                    </CardList>
                </Stack>
            </Stack>
        )
    }

    return (
        <View style={styles.root} testID={testID}>
            <ConversationHeader
                bare
                name={TEXTS.title}
                end={<ConversationClose onPress={onClose} accessibilityLabel={TEXTS.close} />}
            />
            <ScrollView style={styles.scroll} contentContainerStyle={styles.content(floatingRoom)}>
                <View style={styles.column}>{body}</View>
            </ScrollView>
            {endAction != null && (
                <View
                    style={styles.footer(bottomInset ? insets.bottom : 0)}
                    onLayout={(event) => footerBase(event.nativeEvent.layout.height)}
                >
                    <View style={styles.column}>
                        <Button label={endAction.label} onPress={endAction.onPress} fullWidth />
                    </View>
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0 },
    scroll: { flex: 1 },
    /** La bulle tandem du live, au-dessus du pied : la fin de la correction remonte au-dessus d'elle. */
    content: (floatingRoom: number) => ({
        paddingHorizontal: theme.spacing["4"],
        paddingTop: theme.spacing["2"],
        paddingBottom: floatingRoom > 0 ? floatingRoom + theme.spacing["8"] : theme.spacing["4"],
    }),
    /** La colonne du fil de Sam : la correction ne s'étire pas plus large que ses bulles. */
    column: {
        width: "100%",
        maxWidth: CONVERSATION_MAX_WIDTH,
        alignSelf: "center",
    },
    /** Le pied fixe des pages du quiz (`<PageTemplate footer>`) : sa surface et son filet. */
    footer: (bottomInset: number) => ({
        paddingHorizontal: theme.spacing["4"],
        paddingTop: theme.spacing["2"],
        paddingBottom: Math.max(bottomInset, theme.spacing["2"]),
        backgroundColor: theme.colors["surface-raised"],
        borderTopColor: theme.colors.border,
        borderTopWidth: 1,
    }),
}))
